import { logger } from '@ecosystem/newron-client-lib';
import { AuthResultV2, fetchAuth, ResourceCodeV2 } from './persona.client';
import { Periodicity } from '../types';
import { Proposition } from '@ecosystem/newron-client-collections';

interface CellTrackingValueParams {
  proposition: string;
  authResult: AuthResultV2;
  cell: string;
  periodicity: string;
}

const orderedProductList = ['predict', 'predict_lite', 'market'];

const TRACKING_CODE_PREDICT = 'gfknewron';
const TRACKING_CODE_PREDICT_LITE = 'gfknewron_lite';
const TRACKING_CODE_MARKET = 'Digital MI';
const TRACKING_CODE_SUPPLY_CHAIN = 'Supply Chain';

export const AccessProductCode = {
  Market: 'pos',
  DistributionPanel: 'distributor',
} as const;

let cachedQueryString = '';
let cachedAccessValue: string | string[] = '';

/**
 * Returns the auth url for a given combination of cells and periodicity.
 * Use this value in 'doAuthCall'.
 *
 * @param cells
 * @param periodicity
 * @returns
 */
const getAuthQueryString = (cells: string[], periodicity: string) => {
  const params = orderedProductList
    .map(value => `product=${value}`)
    .concat('outlet_group_level=brand')
    .concat(cells.map(c => 'cell=' + c))
    .concat(`periodicity=${periodicity}`);

  const urlParams = new URLSearchParams(params.join('&'));
  return urlParams.toString();
};

/**
 * Returns the query string for auth/v2 endpoint calls.
 * @param proposition
 * @param cells
 * @param periodicity
 * @returns
 */
export const getAuthQueryStringV2 = (proposition: string, cells: string[], periodicity: string): string => {
  const cellParams = cells.map(cell => `cell=${cell}`).join('&');
  const isDistributionPanel = proposition === Proposition.DistributionPanel;
  const productCode = isDistributionPanel ? AccessProductCode.DistributionPanel : AccessProductCode.Market;
  return `product_code=${productCode}&periodicity=${periodicity}&${cellParams}`;
};

/**
 * counts deliveries per year for less-than-monthly subscription support
 * since we might not have complete years we use the max value - it should contain 1 full year of deliveries
 * @param dates
 * @returns
 */
function getDeliveryCount(dates: string[]): number {
  const countPerYear: Record<string, number> = dates
    .map(date => date.split('-')[0])
    .reduce((acc, curr) => {
      const newCount = (acc[curr] || 0) + 1;
      return { ...acc, [curr]: newCount };
    }, {});
  return Math.max(...Object.values(countPerYear), 0);
}

/**
 * Returns the tracking value for a single cell
 * @param proposition
 * @param results
 * @param cell
 * @param periodicity
 * @returns
 */
function getCellTrackingValue({ proposition, authResult, cell, periodicity }: CellTrackingValueParams): string {
  const approved = authResult.approved.find(entry => entry.cell === cell);
  const hasPredict = !!approved?.resources[ResourceCodeV2.PosKpiTPR];
  if (hasPredict) {
    return TRACKING_CODE_PREDICT;
  }
  const hasPredictLite = !!approved?.resources[ResourceCodeV2.PosKpiRevenuePremium];
  if (hasPredictLite) {
    return TRACKING_CODE_PREDICT_LITE;
  }

  const hasMarket = !!approved?.resources[ResourceCodeV2.PosDimensionMarket];
  const hasDistribution = !!approved?.resources[ResourceCodeV2.DistributorFull];
  if (!hasMarket && !hasDistribution) {
    return '';
  }

  const isDistributionPanel = proposition === Proposition.DistributionPanel;

  const trackingCode = isDistributionPanel ? TRACKING_CODE_SUPPLY_CHAIN : TRACKING_CODE_MARKET;

  const reducedDataConfig =
    periodicity === Periodicity.Monthly
      ? approved?.config?.['reduced_data.months']
      : approved?.config?.['reduced_data.weeks'];
  const deliveryCount = getDeliveryCount(reducedDataConfig || []);
  const deliverySuffix = deliveryCount > 0 ? ` ${deliveryCount}x` : '';
  return `${trackingCode}${deliverySuffix}`;
}

/**
 * Calls the persona service to retrieve tracking information for each cell
 *
 * @param proposition
 * @param cells
 * @param periodicity
 * @returns
 */
export const getAccessValueForTracking = async (
  proposition: string,
  cells: string[],
  periodicity: string
): Promise<string | string[]> => {
  return getValueV2(proposition, cells, periodicity);
};

const getValueV2 = async (proposition: string, cells: string[], periodicity: string): Promise<string | string[]> => {
  const queryString = getAuthQueryStringV2(proposition, cells, periodicity);
  if (queryString === cachedQueryString) {
    return cachedAccessValue;
  }

  // no cache - fetch from persona service
  const authResult = await fetchAuth<AuthResultV2>('v2/auth', queryString, proposition);

  cachedAccessValue = cells.map(cell => getCellTrackingValue({ proposition, authResult, cell, periodicity }));
  cachedQueryString = queryString;

  return cachedAccessValue;
};

/**
 * for getting tracking tags for platform tracking funtion
 * @param cells
 * @param periodicity
 * @returns string | string []
 */
export const getAccessValueForTrackingTags = (cells: string[], periodicity: string): string | string[] => {
  const queryString = getAuthQueryString(cells, periodicity);
  if (queryString === cachedQueryString) {
    return cachedAccessValue;
  } else {
    logger.warning('[getAccessValueForTrackingTags] access in cache not found');
    return [];
  }
};

/**
 * Clears the analytics access cache so that the next call to getAccessValueForTracking will refetch from the persona service.
 */
export const clearAccessValueCache = () => {
  cachedQueryString = '';
  cachedAccessValue = '';
};
